import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ArtifactService, HomeService } from 'src/app/services';
import { Home, HomeIfcFile, ListHomesByCompanyRequest, ListHomesByCompanyResponse, ListIfcFilesByHomeIdRequest, ListIfcFilesByHomeIdResponse } from 'situview-webapi-client';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ICompany } from 'identity-webapi-client';
import { Artifact, ListArtifactsRequest, ListArtifactsResponse } from 'situ-warpapi-client';

@Component({
  selector: 'situ-furnish-menu-dialog',
  templateUrl: './situ-furnish-menu-dialog.component.html',
  styleUrls: ['./situ-furnish-menu-dialog.component.less']
})
export class SituFurnishMenuDialogComponent {
  searchMode: boolean = false;
  
  // homes
  searchField = new FormControl();
  homes: Home[] = [];
  filteredHomes: Home[] = [];
  selectedHome: Home | undefined;

  // ifc files
  searchIfcField = new FormControl();
  ifcFiles: HomeIfcFile[] = []
  filteredIfcFiles: HomeIfcFile[] = [];
  selectedIfc: HomeIfcFile | undefined;

  // artifacts
  searchArtifactsField = new FormControl();
  artifacts: FurnishMenuArtifact[] = []
  filteredArtifacts: FurnishMenuArtifact[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SituFurnishMenuDialogDialogData,
    private dialogRef: MatDialogRef<SituFurnishMenuDialogComponent>,
    private homeService: HomeService,
    private snackBar: MatSnackBar,
    private artifactService: ArtifactService) {
    this.loadHomes()
      .then(() => {
        let home = this.homes?.find(e => { return e.name?.toLowerCase() === data?.home });
        if (home?.id) {
          this.searchMode = true;
          this.selectedHome = home;
          this.loadIfcFiles(home.id);
        }
      });
    this.loadArtifacts();
  }

  selectHome(home: Home) {
    if (home?.id && !this.selectedHome) {
      this.selectedHome = home;
      this.loadIfcFiles(home.id);
    }
  }
  
  selectIfc(ifc: HomeIfcFile) {
    if (ifc?.id) {
      this.selectedIfc = ifc;
    }
  }
  
  selectArtifact(artifact: FurnishMenuArtifact) {
    if (artifact?.home && artifact?.ifcName) {
      let home = this.homes.find(x => x.name?.toLowerCase() === artifact.home);
      if (home?.id) {
        this.selectedHome = home;
        this.loadIfcFiles(home.id)
          .then(() => {
            let ifcFile = this.ifcFiles.find(x => x.name?.toLowerCase() === artifact.ifcName);
            if (ifcFile?.id) {
              this.selectedIfc = ifcFile;
            }
          });
      }
    }
  }

  clearIfc() {
    this.selectedIfc = undefined;
  }

  clearHome() {
    this.clearIfc();
    this.selectedHome = undefined;
  }

  toggleSearchMode() {
    this.searchMode = !this.searchMode;
  }

  doFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredHomes = this.homes?.filter(e => { return e.name?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredHomes = this.homes;
    }
  }
  
  doIfcFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredIfcFiles = this.ifcFiles?.filter(e => { return e.name?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredIfcFiles = this.ifcFiles;
    }
  }

  doArtifactFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredArtifacts = this.artifacts?.filter(e => { return e.callerReferenceKey?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredArtifacts = this.artifacts;
    }
  }

  openSituFurnish(ifc: HomeIfcFile) {
    if (!ifc) return;
    this.dialogRef.close(ifc.id);
  }

  loadArtifacts() {
    if (!this.data?.company?.id) {
      return Promise.resolve();
    }

    let request = new ListArtifactsRequest();
    request.companyCode = this.data.company.code;
    request.keyName = "placement";
    request.pageNumber = 0;
    request.pageSize = 50;
    request.isActive = true;

    return this.artifactService.ListArtifacts(request)
      .then((response: ListArtifactsResponse) => {
        if (response.success && response.artifacts) {
          const grouped = response.artifacts.reduce((grouped: any, artifact: Artifact) => {
            if (!grouped[artifact.callerReferenceKey!]) {
                grouped[artifact.callerReferenceKey!] = [];
            }
            grouped[artifact.callerReferenceKey!].push(artifact);
            return grouped;
          }, {});
          
          let latestOnly = (Object.values(grouped) as Artifact[][]).map((group: Artifact[]) => {
            return group.reduce((highest, current) => {
                return (highest.version! > current.version!) ? highest : current;
            });
          });

          this.artifacts = latestOnly.map((x: Artifact) => {
            let crkSplit = x.callerReferenceKey?.split("\\");
            return new FurnishMenuArtifact(x.callerReferenceKey!, crkSplit![2], crkSplit![3] === "master", crkSplit![4], x.dateAdded!);
          });
          this.filteredArtifacts = [...this.artifacts];
        } else {
          this.showMessage("Failed to load artifacts.");
        }
      })
      .catch((err: any) => {
        this.showMessage("Failed to load artifacts.");
      });
  }
  
  loadIfcFiles(homeId: string) {
    if (!this.data?.company?.id || !this.selectedHome) {
      return Promise.resolve();
    }

    let request = new ListIfcFilesByHomeIdRequest();
    request.companyId = this.data.company.id;
    request.homeId = homeId;

    return this.homeService.ListIfcFilesByHomeId(request)
      .then((response: ListIfcFilesByHomeIdResponse) => {
        if (response.success && response.ifcFiles) {
          if (this.data?.ifcFiles) {
            var arr = this.data.ifcFiles.split(',');
            this.ifcFiles = response.ifcFiles.filter(e => { return (e.name) ? arr.indexOf(e.name.toLowerCase()) >= 0 : false; });
          }
          else {
            this.ifcFiles = response.ifcFiles;
          }
          this.filteredIfcFiles = this.ifcFiles;
        } else {
          this.showMessage("Failed to load ifc files.");
        }
      })
      .catch((err: any) => {
        this.showMessage("Failed to load ifc files.");
      });
  }

  private loadHomes() {
    if (!this.data?.company?.id) {
      return Promise.resolve();
    }

    let request = new ListHomesByCompanyRequest();
    request.companyId = this.data.company.id;
    request.pageSize = 1000;

    return this.homeService.ListHomesByCompany(request)
      .then((homes: Home[]) => {
        this.homes = homes;
        this.filteredHomes = [...this.homes];
      })
      .catch((err: any) => {
        this.showMessage("Failed to load homes.");
      });
  };

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class FurnishMenuArtifact {
  constructor(public callerReferenceKey: string, public home: string, public isMasterFile: boolean, public ifcName: string, public dateAdded: Date) {
  }
} 

export interface SituFurnishMenuDialogDialogData {
  company: ICompany;
  home: string | undefined;
  ifcFiles: string | undefined;
}
